/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { dimensionInterpolations, DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { paddingInterpolations, PaddingProps } from '../utils/UNSAFE_interpolations/padding';
import { colorInterpolations, ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { AriaProps, ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import { baseStyle } from './themes/CardStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;
type Props = StyleProps & {
  children?: ComponentChildren;
};

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(paddingInterpolations),
  ...Object.values(ariaInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * A card is a simple container that renders a group of related information in a styled rectangular area.
 * Width, height, padding, and background color are set through props.
 * The aria role for card is normally "group" but this changes to "button" for action cards.
 */

export const Card = ({ children, ...rest }: Props) => {
  const {
    class: mergedClasses,
    ariaLabel,
    ariaLabelledBy,
    ...mergedStyles
  } = mergedInterpolations(rest);
  const styles = classNames([baseStyle, mergedClasses]);

  return (
    <div
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      role="group"
      className={styles}
      style={mergedStyles}>
      {children}
    </div>
  );
};
